import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Loader2, MessageSquare, Paperclip, X } from "lucide-react";
import PrivateAttachment from "@/components/PrivateAttachment";
import ProfileAvatar from "@/components/profile/ProfileAvatar";

const EXPERT_KINDS = [
  ["question", "Stil spørgsmål"],
  ["photo_request", "Bed om billede"],
  ["video_request", "Bed om video"],
  ["call_suggestion", "Foreslå kort afklaringsopkald"],
];

const kindLabel = {
  question: "Spørgsmål",
  information: "Svar",
  photo_request: "Ønsker et billede",
  video_request: "Ønsker en video",
  call_suggestion: "Foreslår et kort opkald",
  message: "Besked",
};

export default function ProblemClarification({
  problemId,
  role = "customer",
  expertUserId,
  className = "",
}) {
  const [items, setItems] = useState([]);
  const [content, setContent] = useState("");
  const [kind, setKind] = useState(role === "expert" ? "question" : "information");
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const threadParams = expertUserId ? { expert_user_id: expertUserId } : {};

  const markRead = async () => {
    try {
      await api.post(`/problems/${problemId}/clarifications/read`, null, { params: threadParams });
      window.dispatchEvent(new CustomEvent("messages:read", { detail: { problemId, expertUserId } }));
    } catch {
      // The conversation can still load if updating read state temporarily fails.
    }
  };

  const reload = () => api.get(`/problems/${problemId}/clarifications`, { params: threadParams })
    .then((response) => setItems(response.data || []))
    .catch((requestError) => {
      if (requestError.response?.status !== 404) {
        setError(requestError.response?.data?.detail || "Samtalen kunne ikke hentes.");
      }
    });

  useEffect(() => {
    if (role === "customer" && !expertUserId) {
      setItems([]);
      return undefined;
    }
    const syncConversation = () => markRead().then(reload);
    syncConversation();
    const timer = setInterval(syncConversation, 6000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problemId, expertUserId, role]);

  const addFiles = async (files) => {
    const selected = Array.from(files || []).slice(0, Math.max(0, 6 - attachments.length));
    if (!selected.length) return;
    setUploading(true);
    setError("");
    try {
      for (const file of selected) {
        if (!/^(image|video)\//.test(file.type || "")) {
          throw new Error("Kun billeder og korte videoer kan vedhæftes.");
        }
        if (file.size > 25 * 1024 * 1024) {
          throw new Error("Filen er for stor. Maksimum er 25 MB.");
        }
        const body = new FormData();
        body.append("file", file);
        if (expertUserId) body.append("expert_user_id", expertUserId);
        const { data } = await api.post(
          `/problems/${problemId}/clarifications/upload`,
          body,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        setAttachments((current) => [
          ...current,
          { ...data, local_url: URL.createObjectURL(file) },
        ]);
      }
    } catch (requestError) {
      setError(
        requestError.response?.data?.detail
        || requestError.message
        || "Filen kunne ikke uploades.",
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (attachmentId) => {
    setAttachments((current) => {
      const removed = current.find((attachment) => attachment.id === attachmentId);
      if (removed?.local_url) URL.revokeObjectURL(removed.local_url);
      return current.filter((attachment) => attachment.id !== attachmentId);
    });
  };

  const send = async (event) => {
    event.preventDefault();
    const message = content.trim();
    if (!message && attachments.length === 0) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/problems/${problemId}/clarifications`, {
        content: message,
        kind: role === "expert" ? kind : "information",
        expert_user_id: expertUserId || null,
        attachment_ids: attachments.map((attachment) => attachment.id),
      });
      attachments.forEach((attachment) => {
        if (attachment.local_url) URL.revokeObjectURL(attachment.local_url);
      });
      setContent("");
      setAttachments([]);
      setItems((current) => [...current, data]);
      await reload();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Beskeden kunne ikke sendes.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`rounded-2xl border border-brand-line bg-white p-4 sm:p-5 ${className}`} data-testid={`clarification-${problemId}`}>
      <div className="flex items-center gap-2">
        <MessageSquare size={16} className="text-brand-green"/>
        <h3 className="font-medium text-brand-ink">Afklaring før prisforslag</h3>
      </div>
      <p className="mt-1 text-xs text-brand-muted">Spørgsmål, svar og vedhæftninger her er gratis og knyttet til denne forespørgsel.</p>

      {items.length > 0 ? (
        <div className="mt-3 space-y-2 max-h-[32rem] overflow-y-auto">
          {items.map((item) => (
            <div key={item.id} className={`flex gap-2.5 ${item.sender_role === role ? "ml-5 flex-row-reverse" : "mr-5"}`}>
              <ProfileAvatar name={item.sender_name} photoUrl={item.sender_photo_url} size="chat" className="shrink-0"/>
              <div className={`min-w-0 flex-1 rounded-xl p-3 text-sm ${item.sender_role === role ? "bg-brand-soft" : "bg-white border border-brand-line"}`}>
              <div className="text-[10px] uppercase tracking-widest text-brand-muted">
                {item.sender_name} · {kindLabel[item.kind] || "Besked"}
              </div>
              {item.content && <div className="mt-1 text-brand-ink whitespace-pre-wrap">{item.content}</div>}
              {(item.attachments || []).length > 0 && (
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  {item.attachments.map((attachment) => <PrivateAttachment key={attachment.id} attachment={attachment}/>) }
                </div>
              )}
              <div className="mt-1.5 text-[10px] text-brand-muted">{item.created_at ? new Date(item.created_at).toLocaleString("da-DK", { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" }) : ""}</div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-3 rounded-lg border border-dashed border-brand-line p-3 text-sm text-brand-muted">
          Ingen afklaringsbeskeder i denne tråd endnu.
        </div>
      )}

      <form onSubmit={send} className="mt-3">
        {role === "expert" && (
          <select value={kind} onChange={(event) => setKind(event.target.value)} className="w-full card-outline px-3 py-2 text-sm bg-white">
            {EXPERT_KINDS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        )}
        <textarea
          value={content}
          onChange={(event) => setContent(event.target.value)}
          rows={3}
          maxLength={2000}
          className="mt-2 w-full card-outline px-3 py-2 text-sm resize-y bg-white"
          placeholder={role === "expert" ? "Hvad mangler du for at kunne vurdere opgaven?" : "Skriv dit svar eller tilføj flere oplysninger"}
        />

        {attachments.length > 0 && (
          <div className="mt-2 grid sm:grid-cols-2 gap-2">
            {attachments.map((attachment) => (
              <div key={attachment.id} className="relative">
                <PrivateAttachment attachment={attachment} pending/>
                <button
                  type="button"
                  onClick={() => removeAttachment(attachment.id)}
                  className="absolute top-1 right-1 rounded-full bg-white p-1 shadow"
                  aria-label="Fjern vedhæftning"
                >
                  <X size={12}/>
                </button>
              </div>
            ))}
          </div>
        )}

        {error && <div className="mt-2 text-xs text-[#991B1B]">{error}</div>}
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={(event) => addFiles(event.target.files)}
            className="hidden"
            data-testid="clarification-file-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || attachments.length >= 6}
            className="btn-secondary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="clarification-attach"
          >
            {uploading ? <Loader2 size={14} className="animate-spin"/> : <Paperclip size={14}/>}
            {uploading ? "Uploader…" : "Vedhæft billede eller video"}
          </button>
          <button
            type="submit"
            disabled={busy || uploading || (!content.trim() && attachments.length === 0)}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {busy ? "Sender…" : "Send besked"}
          </button>
          <span className="text-[11px] text-brand-muted">Maks. 6 filer · 25 MB pr. fil</span>
        </div>
      </form>
    </div>
  );
}

